import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import ClientAppreciationAddEditModal from "pages/AllCommonModal/ClientAppreciationAddEditModal/client-appreciation-add-edit-modal";
import { useClientAppreciationContext } from "libs/context/ClientAppreciationContext";
import {
  addClientAppreciation,
  submitClientAppreciationFilterData,
  updateClientAppreciation,
} from "model/ClientAppreciationModel";
import { getDefaultUserList } from "model/UserModel";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeClientAppreciationFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "clientId", "userId", "fromDate", "toDate"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete out[key];
  });
  return out;
};

const ClientAppretiation = () => {
  const { permissionData } = usePermissionContext() || {};
  const { clientId } = useParams();
  const { fetchClientAppreciationContextData, clientAppreciationContextData } =
    useClientAppreciationContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("ClientAppreciation");
  const initialFilterFetchedRef = useRef(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    userId: "",
    fromDate: "",
    toDate: "",
  });
  const [clientAppreciationData, setClientAppreciationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [clientAppreciationAddEditModalOpen, setClientAppreciationAddEditModalOpen] =
    useState(false);
  const [editableData, setEditableData] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    clientId: null,
    userId: null,
    fromDate: null,
    toDate: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const filteredData = useMemo(() => clientAppreciationData, [clientAppreciationData]);

  const clientAppreciationActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.ClientAppreciationUpdateAccess),
      canEdit: Boolean(permissionData?.ClientAppreciationUpdateAccess),
    }),
    [permissionData]
  );

  const hasAnyClientAppreciationRowAction = useMemo(
    () => clientAppreciationActionPermissions.canEdit,
    [clientAppreciationActionPermissions]
  );

  const clientAppreciationFilterModalData = useMemo(
    () =>
      mapFilterDefinitionsToModalData(
        clientAppreciationContextData?.filters || [],
        filterForm
      ),
    [clientAppreciationContextData?.filters, filterForm]
  );

  useEffect(() => {
    fetchClientAppreciationContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getClientAppreciationList({
      pageNumber: 1,
      pageSize,
      ...(clientId ? { clientId: strOrNull(clientId) } : {}),
    });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = clientAppreciationContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: pascalToCamel(col?.field),
      type: col?.type,
    }));
    setTableMetaData(valueReModified || []);
  }, [clientAppreciationContextData]);

  const getClientAppreciationList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = normalizeClientAppreciationFilterPayload({
      pageNumber: requestPayload.pageNumber,
      pageSize: requestPayload.pageSize,
      nameContains: requestPayload.nameContains,
      clientId: requestPayload.clientId,
      userId: requestPayload.userId,
      fromDate: requestPayload.fromDate,
      toDate: requestPayload.toDate,
      ...payloadOverrides,
    });
    delete payload.search;
    if (payload?.user && !payload?.userId) {
      payload.userId = payload.user;
    }
    delete payload.user;
    const response = await submitClientAppreciationFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || responseData || [];

    const valueReModified = (Array.isArray(listData) ? listData : []).map((item = {}) => ({
      ...item,
      id: item?.id || item?._id || null,
      client: item?.client || item?.clientName || null,
      for: item?.for || item?.forName || null,
      note: item?.note || item?.notes || null,
      date: item?.date || item?.createdOn || null,
      clientName: item?.client,
      status: item?.status || null,
    }));

    if (response) {
      setClientAppreciationData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(normalizeClientAppreciationFilterPayload(payload));
    } else {
      setClientAppreciationData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (modalFormPayload = {}) => {
    const mergedFilterForm = mergeModalSubmitIntoListFilter(
      filterForm,
      clientAppreciationFilterModalData,
      modalFormPayload
    );
    setFilterForm(mergedFilterForm);
    await getClientAppreciationList({
      ...buildPayloadFromUpperForm(mergedFilterForm),
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const buildPayloadFromUpperForm = (form = {}) =>
    normalizeClientAppreciationFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(form?.search),
      clientId: strOrNull(form?.clientId),
      userId: strOrNull(form?.userId),
      fromDate: strOrNull(form?.fromDate),
      toDate: strOrNull(form?.toDate),
    });

  const handleUpperSearch = () => {
    getClientAppreciationList(buildPayloadFromUpperForm(filterForm));
  };

  const handleFilterFieldChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePageChange = async (page) => {
    await getClientAppreciationList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getClientAppreciationList({ pageNumber: 1, pageSize: rows });
  };

  const handleEdit = (row = {}) => {
    setEditableData(row || null);
    setClientAppreciationAddEditModalOpen(true);
  };

  const handleAddAppreciation = () => {
    setEditableData(clientId ? { clientId } : null);
    setClientAppreciationAddEditModalOpen(true);
  };

  const handleAddEditSubmit = async (modalData) => {
    const payload = {
      clientId: modalData?.clientId || filterForm?.clientId || clientId || null,
      userId: modalData?.userId || null,
      user: modalData?.user || modalData?.userId || null,
      date: modalData?.date || null,
      note: modalData?.note || null
    };

    const response = modalData?.id
      ? await updateClientAppreciation(modalData?.id, payload)
      : await addClientAppreciation(payload);

    if (response?.success) {
      toast.success(response?.message || "Saved successfully");
      await getClientAppreciationList({ pageNumber: 1, pageSize: paginationMeta?.pageSize || DEFAULT_PAGE_SIZE });
    } else {
      toast.error(response?.message || "Unable to save");
    }
  };

  const getUserList = async () => {
    setLoading(true);
    const response = await getDefaultUserList({});
    if (response) {
      const userRecords = response?.data?.records || response?.data || [];
      setUserOptions(
        (userRecords || []).map((item) => ({
          value: item?.id || item?._id || item?.userId || "",
          label: item?.userName || item?.name || item?.email || "User",
        }))
      );
    } else {
      setUserOptions([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUserList()
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Client Appreciation</h5>
                {clientId && clientAppreciationActionPermissions.canCreate ? (
                  <div className="d-flex justify-content-end mb-3">
                    <Button color="primary" onClick={handleAddAppreciation}>
                      <i className="mdi mdi-plus me-1"></i>
                      Add Appreciation
                    </Button>
                  </div>
                ) : null}
              </div>
              <hr />

                            <LegacyFilterToolbar>
              <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--text">
                  <span className="manage-user-filter-label">Search</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="text"
                    name="search"
                    placeholder="Search by Email/Company etc."
                    className={LEGACY_FILTER_INPUT_CLASS}
                    value={filterForm.search}
                    onChange={(e) => {
                      const nextValue = e?.target?.value || "";
                      setFilterForm((prev) => ({ ...prev, search: nextValue }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleUpperSearch();
                      }
                    }}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                  <span className="manage-user-filter-label">Client</span>
                  <div className="metadata-filter-field__control">
                  <AsyncClientSelect
                    key={`client-appreciation-client-${filterForm.clientId}`}
                    pageName="ClientMenu"
                    value={filterForm.clientId}
                    initialOption={buildClientAsyncFilterInitialOption(
                      filterForm.clientId,
                      filterForm[getClientAsyncFilterLabelFieldKey()]
                    )}
                    onChange={(option) =>
                      setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                    }
                    placeholder="Select Client"
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                  <span className="manage-user-filter-label">User</span>
                  <div className="metadata-filter-field__control">
                  <AsyncUserSelect
                    pageName="ClientAppreciation"
                    value={filterForm.userId}
                    onChange={(option) =>
                      setFilterForm((prev) => ({
                        ...prev,
                        userId: option?.value ?? "",
                      }))
                    }
                    placeholder="Select User"
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">From date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="fromDate"
                    value={filterForm.fromDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                  <span className="manage-user-filter-label">To date</span>
                  <div className="metadata-filter-field__control">
                  <Input
                    type="date"
                    name="toDate"
                    value={filterForm.toDate}
                    onChange={handleFilterFieldChange}
                    className={LEGACY_FILTER_INPUT_CLASS}
                  />
                  </div>
                </Col>
                <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                  <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                  <div className="metadata-filter-toolbar__actions">
                  <Button
                    color="primary"
                    className="w-100 manage-user-search-btn"
                    onClick={handleUpperSearch}
                  >
                    <i className="fa fa-search me-1"></i>
                    Search
                  </Button>
                  </div>
                </Col>
              </Row>
              </LegacyFilterToolbar>


              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyClientAppreciationRowAction}
                  onEdit={
                    clientAppreciationActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isEditAccess={clientAppreciationActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"ClientAppreciation"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Client Appreciation"
            handleSubmit={handleFilterSubmit}
            filterData={clientAppreciationFilterModalData}
            pageName="ClientAppreciation"
          />
          <ClientAppreciationAddEditModal
            clientAppreciationAddEditModalOpen={clientAppreciationAddEditModalOpen}
            setclientAppreciationAddEditModalOpen={setClientAppreciationAddEditModalOpen}
            editableData={editableData}
            onSubmit={handleAddEditSubmit}
            userOptions={userOptions}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

ClientAppretiation.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ClientAppretiation);
